// Pantalla — the webapp's display settings that are the reader's own preferences on the server
// (`display_settings` category), so they follow the person to every device: collapsed reply threads
// (only when the server lets the user choose), the clock, how names are shown (unless locked), and
// link previews. The server echoes `preferences_changed`; the engine re-reads its policies on it.
import { data } from '../../../bridge/client';
import { nameFormatChoice, nameFormats, threadsChoice } from '../../../core/accountSettings';
import { t } from '../../../i18n/i18n';
import { RadioRow, SettingsGroup, SwitchRow, useAccount, useSaver } from './parts';

export function DisplaySection() {
  const { prefs, setPrefs } = useAccount();
  const threads = useSaver();
  const clock = useSaver();
  const names = useSaver();
  const previews = useSaver();
  if (!prefs) return <div className="st-skeleton" aria-busy="true" />;

  const save = (saver: ReturnType<typeof useSaver>, name: string, value: string) => {
    const before = prefs;
    setPrefs({ ...prefs, display: { ...prefs.display, [name]: value } });
    saver.run(
      () => data.call('accountSavePreferences', [{ category: 'display_settings', name, value }]),
      () => setPrefs(before),
    );
  };

  const crt = threadsChoice(prefs.config.threads, prefs.display.collapsed_reply_threads);
  const nameChoice = nameFormatChoice(prefs.config.nameDisplay, prefs.config.nameDisplayLocked, prefs.display.name_format);
  const military = prefs.display.use_military_time === 'true';
  const linkPreviews = prefs.display.link_previews !== 'false';

  return (
    <>
      <SettingsGroup title={t('web.settings.display.threads')} save={threads.state} footer={crt.editable ? undefined : t('web.settings.display.threadsLocked')}>
        <SwitchRow
          title={t('web.settings.display.threads')}
          subtitle={t('web.settings.display.threadsHint')}
          on={crt.on}
          disabled={!crt.editable}
          onChange={(on) => save(threads, 'collapsed_reply_threads', on ? 'on' : 'off')}
        />
      </SettingsGroup>

      <SettingsGroup title={t('web.settings.display.clock')} save={clock.state}>
        <div role="radiogroup" aria-label={t('web.settings.display.clock')}>
          <RadioRow title={t('web.settings.display.clock12')} checked={!military} onPick={() => military && save(clock, 'use_military_time', 'false')} />
          <RadioRow title={t('web.settings.display.clock24')} checked={military} onPick={() => !military && save(clock, 'use_military_time', 'true')} />
        </div>
      </SettingsGroup>

      <SettingsGroup title={t('web.settings.display.names')} save={names.state} footer={nameChoice.editable ? undefined : t('web.settings.display.namesLocked')}>
        <div role="radiogroup" aria-label={t('web.settings.display.names')}>
          {nameFormats.map((f) => (
            <RadioRow key={f} title={t(`web.settings.display.name.${f}`)} checked={nameChoice.value === f} disabled={!nameChoice.editable} onPick={() => nameChoice.value !== f && save(names, 'name_format', f)} />
          ))}
        </div>
      </SettingsGroup>

      <SettingsGroup title={t('web.settings.display.linkPreviews')} save={previews.state} footer={t('web.settings.serverWide')}>
        <SwitchRow title={t('web.settings.display.linkPreviews')} subtitle={t('web.settings.display.linkPreviewsHint')} on={linkPreviews} onChange={(on) => save(previews, 'link_previews', on ? 'true' : 'false')} />
      </SettingsGroup>
    </>
  );
}
